import Alert from "./alert"
import ObjCheck from "./objCheck"
import { ReportField, ReportFillingType, ReportType, SOURCES_PRIORITY } from "../constnats/reportConstants"
import { RESOURCE_SOURCE_TYPES_SHORT_FORMS, RESOURCE_TYPE_FORMS_DOUBLE_MAP, ResourceCategory } from "../constnats/resourceConstants"

/**
 * 
 * @param {{[ReportField.YEAR], [ReportField.TYPE], [ReportField.PERIOD]}} report Required 
 * @param {int|string} companyId  Required
 * @returns not-null
 */
function getResourceFilter(report, companyId) {

    const year = report[ReportField.YEAR]
    const latestFiling = report[ReportField.TYPE] === ReportFillingType.LATEST_FILLING

    return {
        companyId: companyId,
        year: latestFiling ? Number(year) + 1 : year,
        quoter: getGetPeriod(report[ReportField.PERIOD]),
        excludeOriginalOnly: latestFiling
    }
}


/**
 * 
 * @param {string} period Rquired 
 * @returns not-null
 */
function getGetPeriod(period) {

    let resourcePeriod
    if (period === "Q1") {
        resourcePeriod = "1"
    } else if (period === "H1" || period === "Q2") {
        resourcePeriod = "2"
    } else if (period === "Q3" || period === "9M") {
        resourcePeriod = "3"
    } else if (period === "Q4" || period === "H2" || period === "FY") {
        resourcePeriod = "4"
    } else {
        console.error("Unexpected period:" + period)
    }

    return resourcePeriod
}


/**
 * @param {string} path File path requred 
 * @returns {ResourceProps} not-null 
 */
function toResourceProps(path) {
    const fileNameStartIdx = path.lastIndexOf("/") + 1
    const fileName = path.slice(fileNameStartIdx - path.length)
    const originalOnly = path.includes('/original_only/');

    return ResourceProps.deserialize(fileName, originalOnly)
}

const removeFileExtension = path => {
    const extensionStartIdx = path.lastIndexOf(".")
    return path.slice(0, extensionStartIdx)
}

/**
 * Regex pattern to match numbers that appear after the letter 'p'
 *
 * (?<=(?:p))[0-9]+ - Matches one or more digits that are preceded by 'p'
 * (?<=...) - Positive lookbehind assertion; matches a position that is preceded by the pattern inside
 * (?:p) - Non-capturing group matching the literal character 'p'
 * [0-9]+ - Matches one or more digits (0 through 9)
 *
 */
const REGEX_NUMBERS_AFTER_P = /(?<=(?:p))[0-9]+/;

/**
 * 
 * @param {string} suffix 
 */
function getPageFromResourceSuffix(suffix) {
    return Number(suffix.match(REGEX_NUMBERS_AFTER_P)?.[0] ?? 0)
}

/**
 * @param {string} sourceTypeFullForm
 * @return {number} 
 */

function getResourceSourceTypeSortPriority(sourceTypeFullForm) {
    return SOURCES_PRIORITY[sourceTypeFullForm] ?? Infinity;
}

/**
 * Returns a new array with sorted elements
 */
function sortResourceInfos(resourceInfos) {
    const sortedResourceInfos = resourceInfos.map(info => {
        const infoResourceProps = toResourceProps(info.path);
        return {
            info: info,
            sortMetadata: {
                userFriendlyName: ResourceProps.userFriendlyName(infoResourceProps, false, false),
                suffix: infoResourceProps.suffix,
                sourceTypeSortPriority: getResourceSourceTypeSortPriority(RESOURCE_TYPE_FORMS_DOUBLE_MAP.getKeyByValue(infoResourceProps.sourceType) ?? "")
            }
        };
    }).sort((a, b) => {
        const aSortMetadata = a.sortMetadata;
        const bSortMetadata = b.sortMetadata;
        if (aSortMetadata.userFriendlyName === bSortMetadata.userFriendlyName) {

            if(a.info.sourceType === b.info.sourceType) {   
                return (
                    getPageFromResourceSuffix(aSortMetadata.suffix) -
                    getPageFromResourceSuffix(bSortMetadata.suffix)
                );
            }
            return aSortMetadata.sourceTypeSortPriority - bSortMetadata.sourceTypeSortPriority
        }

        return aSortMetadata.userFriendlyName.localeCompare(bSortMetadata.userFriendlyName);
    });
    return sortedResourceInfos.map(info => {
        return info.info
    })
}

function getExtension(path) {
    const pathArr = path.split(".")
    return pathArr.length > 1 ? (pathArr[pathArr.length - 1].toLowerCase()) : ""
}

class ResourceProps {

    static documentTypesWithoutSourceType = [
        ResourceCategory.REPORT, ResourceCategory.PRESENTATION, ResourceCategory.PRESS_RELEASE, ResourceCategory.REPORT_SUPPLEMENT, ResourceCategory.TEN_Q, ResourceCategory.TEN_K, ResourceCategory.EIGHT_Q, ResourceCategory.EIGHT_K
    ]

    /**
     * NOTE: if the finDocType is not in the list documentTypesWithoutSourceType the provided source type will be used
     * if the finDocType is in the list the source type will be the finDocType
     */
    constructor(finDocType = "", reportType = "", note = false, suffix = "",
        fileExtension = "", uuid = null, originalOnly = false, sourceType = "",) {

        this.finDocType = finDocType
        this.reportType = reportType
        this.note = note
        this.suffix = suffix
        this.fileExtension = fileExtension
        this.uuid = uuid
        this.originalOnly = originalOnly
        /** This field contains the short form of the source type! */
        this.sourceType = ResourceProps.sourceTypeManuallyControlled(finDocType) ? sourceType 
        : RESOURCE_SOURCE_TYPES_SHORT_FORMS[finDocType];
    }

    static valid(props) {
        return (!ObjCheck.isNullUndefinedOrEmpty(props.finDocType))
            && (!ObjCheck.isNullUndefinedOrEmpty(props.reportType))
            && (!ObjCheck.isNullUndefinedOrEmpty(props.sourceType))
    }

    static serialize(props) {
        const uuidPrefix = props.uuid === null ? "" : props.uuid + "--"
        return `${uuidPrefix}${props.finDocType}_${props.reportType}_${props.note ? "t" : "f"}_${props.suffix}_${props.sourceType}.${props.fileExtension}`
    }

    static sourceTypeManuallyControlled (finDocType) {
        return !ResourceProps.documentTypesWithoutSourceType.includes(finDocType)
    }

    /**
    * @param {string} fileName File name with extension Required 
    * @param {boolean} [originalOnly] Optional
    * @returns not-null
    */
    static deserialize(fileName, originalOnly) {
        const uuidEndIdx = fileName.indexOf("--")
        let fileNameWithoutUUID

        if (uuidEndIdx > 0) {
            fileNameWithoutUUID = fileName.slice(uuidEndIdx + 2 - fileName.length)
        } else {
            fileNameWithoutUUID = fileName
        }

        let [finDocType, encodedReportType, isNote, suffix, sourceType] = removeFileExtension(fileNameWithoutUUID).split("_")

        /**
         * Due to a typo here we check if the fin doc type contains the value with the typo
         * if it does we reassign it with the new corrected value
         */
        if(finDocType === ResourceCategory.REPORT_SUPLEMENT) {
            finDocType = ResourceCategory.REPORT_SUPPLEMENT
        }

        if (ObjCheck.isNullUndefinedEmptyOrDash(sourceType) 
            &&  !ResourceProps.sourceTypeManuallyControlled(finDocType)) {
                sourceType = RESOURCE_SOURCE_TYPES_SHORT_FORMS[finDocType]
        }
        
        const reportType = encodedReportType === undefined ? ReportType.STANDARD : encodedReportType
        const note = isNote === "t" ? true : false
        return new ResourceProps(finDocType, reportType, note, suffix,
            getExtension(fileNameWithoutUUID),
            fileName.slice(0, uuidEndIdx), originalOnly, sourceType)
    }

    static userFriendlyName(props, withExtension, withSuffix = true, withSourceType = false) {
        const note = props.note ? " Note" : ""
        const suffix = (withSuffix && !ObjCheck.isNullUndefinedOrEmpty(props.suffix)) ? ` ${props.suffix}` : ""
        const sourceType = (withSourceType && !ObjCheck.isNullUndefinedOrEmpty(props.sourceType) && ResourceProps.sourceTypeManuallyControlled(props.finDocType)) ? `(${props.sourceType})` : ""
        const extension = withExtension ? ("." + props.fileExtension.toLowerCase()) : ""
        return `${props.finDocType} ${props.reportType}${note}${suffix}${sourceType}${extension}`.replace(/\s\s+/g, ' ')
    }
}


function getRelocationPath(oldPath, year, period, originalOnly, resourceProps) {
    const fileName = ResourceProps.serialize(resourceProps)

    if (!ResourceProps.valid(resourceProps)
        || ObjCheck.isNullUndefinedOrEmpty(year)
        || ObjCheck.isNullUndefinedOrEmpty(period)) {
        Alert.warn("The Financial Document Type, Report Type, Year and Period must be configured for all files.")
        return
    }

    const oldPathSections = oldPath.split("/")

    if (oldPathSections.length === 4) {
        oldPathSections[1] = toYearComponent(year)
        oldPathSections[2] = period
        oldPathSections[3] = fileName
        if (originalOnly === true) {
            oldPathSections.splice(3, 0, "original_only")
        }
    } else if (oldPathSections.length === 5) {
        oldPathSections[1] = toYearComponent(year)
        oldPathSections[2] = period
        oldPathSections[4] = fileName
        if (originalOnly === false) {
            oldPathSections.splice(3, 1)
        }
    } else {
        Alert.warn("Unexpected resource path:" + oldPath)
        throw new Error("Unexpected resource path:" + oldPath)
    }

    return oldPathSections.join("/")
}

function toYearComponent(origianlYear) {
    origianlYear = Number(origianlYear)
    return "original_" + origianlYear + "_latest_" + (origianlYear - 1)
}
/** 
 * format original_2018_latest_2017
 */
function getYearsComponent(path) {
    return path.split("/")[1]
}

function getPeriod(path) {
    return path.split("/")[2]
}

function getCompanyId(path) {
    return path.split("/")[0]
}

export {
    getResourceFilter, getGetPeriod, toResourceProps,
    sortResourceInfos, ResourceProps, getExtension, removeFileExtension,
    getRelocationPath, getYearsComponent, getPeriod, getCompanyId
}